// 숙련 창(캐릭터 창 「숙련」 탭)·대장간 초월 구역의 순수 모델(C4). DOM 없음 — 버튼 활성·문구·레이아웃을 여기서 계산하고 masteryPanel.ts가 그린다.
// 데이터와 규칙은 src/shared/mastery.ts, 게임 연결은 src/game/mastery.ts(MasteryController.view = GameView.mastery). 확률(천장 포함)은 화면에 공개한다.
import { planStoneConversion } from '../shared/content/economy';
import type { EquipSlot } from '../shared/content/types';
import {
  MASTERY_EFFECT, MASTERY_MILESTONES, MASTERY_NAME, MASTERY_STATS, MASTERY_STAT_CAP, TRANSCEND_MAX, TRANSCEND_PITY, TRANSCEND_STEPS, TRANSCEND_STONE_TIER,
  TRANSCEND_EFFECT, TRANSCEND_SLOT_KIND, isRespec, masterySum, milestoneClaimed,
  type MasteryAlloc, type MasteryStat, type MasteryView,
} from '../shared/mastery';
import { SLOT_ORDER } from '../shared/gearLedger';
import type { Tone } from './rpgModel';

const SLOT_KO: Readonly<Record<EquipSlot, string>> = { weapon: '무기', helm: '투구', armor: '갑옷', gloves: '장갑', boots: '신발', amulet: '목걸이' };
const num = (n: number): string => Math.round(n).toLocaleString('ko-KR');
export const pctText = (p: number): string => {
  const v = p * 100;
  return (Math.abs(v - Math.round(v)) < 0.05 ? String(Math.round(v)) : v.toFixed(1)) + '%';
};

// ── 숙련 창 ────────────────────────────────────────
export interface MasteryRowModel {
  readonly idx: number;
  readonly stat: MasteryStat;
  readonly name: string;
  /** 1점당 효과 문구 */
  readonly per: string;
  readonly value: number;
  /** 지금 배분의 총 효과 문구 */
  readonly effect: string;
  readonly canDec: boolean;
  readonly canInc: boolean;
}
export interface MilestoneModel {
  readonly idx: number;
  readonly at: number;
  readonly reward: string;
  readonly note: string;
  readonly state: 'claimed' | 'ready' | 'locked';
  readonly label: string;
}
export interface UnlockModel { readonly to: number; readonly label: string; readonly ok: boolean }
export interface MasteryModel {
  readonly ready: boolean;
  readonly level: number;
  readonly into: number;
  readonly need: number;
  readonly ratio: number;
  readonly progressText: string;
  readonly pointsText: string;
  readonly free: number;
  readonly emptyHint: string;
  readonly rows: readonly MasteryRowModel[];
  readonly dirty: boolean;
  readonly respec: boolean;
  readonly cost: number;
  readonly affordable: boolean;
  readonly confirming: boolean;
  readonly applyLabel: string;
  readonly applyDisabled: boolean;
  readonly confirmText: string;
  readonly milestones: readonly MilestoneModel[];
  readonly readyCount: number;
  readonly unlocks: readonly UnlockModel[];
}

/** 해금 조건 문구(TRANSCEND_STEPS의 gate에서 만든다 — 숫자를 여기에 따로 두지 않는다) */
function gateLabel(to: number): string {
  const g = TRANSCEND_STEPS[to - 16].gate;
  const parts: string[] = [];
  if (g.cycle != null) parts.push(`${g.cycle}차 환생`);
  if (g.mastery != null) parts.push(`숙련 ${g.mastery}`);
  return parts.join(' + ');
}

/** 한 능력치 1점당 효과 문구 */
export function perText(s: MasteryStat): string {
  const e = MASTERY_EFFECT[s];
  return s === 'crit' ? `1점당 +${(e * 100).toFixed(1)}%p` : `1점당 +${pctText(e)}`;
}
/** 능력치 v점의 총 효과 문구 */
export function effectText(s: MasteryStat, v: number): string {
  const e = MASTERY_EFFECT[s] * v;
  return s === 'crit' ? `+${(e * 100).toFixed(1)}%p` : `+${pctText(e)}`;
}
export function milestoneRewardText(i: number): string {
  const m = MASTERY_MILESTONES[i];
  return `최상 강화석 ${num(m.stones)} · 보호 주문서 ${m.protects} · ${num(m.gold)} 골드`;
}

/** 한 칸을 ±1 한 새 배분(안 되면 null). 합이 포인트를 넘거나 칸 상한을 넘거나 0 밑이면 null */
export function draftStep(draft: MasteryAlloc, points: number, idx: number, d: 1 | -1): MasteryAlloc | null {
  const v = draft[idx] + d;
  if (v < 0 || v > MASTERY_STAT_CAP) return null;
  if (d > 0 && masterySum(draft) + 1 > points) return null;
  const out = [draft[0], draft[1], draft[2], draft[3]] as [number, number, number, number];
  out[idx] = v;
  return out;
}

/** 보기 + 편집 중 배분(draft) + 확인 단계 → 표시 모델 */
export function masteryModel(view: MasteryView | undefined, draft: MasteryAlloc | null, confirming: boolean, gold: number, usable: boolean): MasteryModel {
  if (!view) {
    return { ready: false, level: 0, into: 0, need: 0, ratio: 0, progressText: '', pointsText: '', free: 0, emptyHint: '숙련 정보를 불러오는 중이에요.', rows: [], dirty: false, respec: false, cost: 0, affordable: false, confirming: false,
      applyLabel: '적용', applyDisabled: true, confirmText: '', milestones: [], readyCount: 0, unlocks: [] };
  }
  const cur = draft ?? view.alloc;
  const dirty = cur.some((v, i) => v !== view.alloc[i]);
  const respec = dirty && isRespec(view.alloc, cur);
  const cost = respec ? view.respecCost : 0;
  const affordable = gold >= cost;
  const free = Math.max(0, view.points - masterySum(cur));
  const rows: MasteryRowModel[] = MASTERY_STATS.map((stat, idx) => ({
    idx, stat, name: MASTERY_NAME[stat], per: perText(stat), value: cur[idx], effect: effectText(stat, cur[idx]),
    canDec: usable && cur[idx] > 0, canInc: usable && draftStep(cur, view.points, idx, 1) !== null,
  }));
  const ready = new Set(view.claimable);
  const milestones: MilestoneModel[] = MASTERY_MILESTONES.map((m, idx) => {
    const claimed = milestoneClaimed(view.claimed, idx);
    const state = claimed ? 'claimed' : ready.has(idx) ? 'ready' : 'locked';
    return { idx, at: m.at, reward: milestoneRewardText(idx), note: m.note ?? '', state, label: state === 'claimed' ? '받음' : state === 'ready' ? '받기' : `숙련 ${m.at}에 열림` };
  });
  const unlocks: UnlockModel[] = TRANSCEND_STEPS.map((st, i) => ({ to: st.to, label: `+${st.to} ${gateLabel(st.to)}`, ok: view.unlock > i }));
  const needRespecConfirm = respec && !confirming;
  return {
    ready: true,
    level: view.level, into: view.into, need: view.need,
    ratio: view.need > 0 ? Math.max(0, Math.min(1, view.into / view.need)) : 1,
    progressText: view.need > 0 ? `${num(view.into)} / ${num(view.need)} EXP` : '최대 숙련',
    pointsText: `포인트 남음 ${free} / ${view.points}`,
    free,
    emptyHint: view.level <= 0 ? 'Lv99 이후 얻는 EXP가 숙련이 됩니다.' : '',
    rows, dirty, respec, cost, affordable, confirming: respec && confirming,
    applyLabel: !dirty ? '변경 없음' : respec ? (needRespecConfirm ? '적용 (재분배)' : '재분배 확인 중') : '적용',
    applyDisabled: !usable || !dirty || (respec && confirming),
    confirmText: respec && confirming ? (affordable ? `재분배 비용 ${num(cost)}골드가 듭니다. 계속할까요?` : `재분배 비용 ${num(cost)}골드 — 골드가 부족해요 (보유 ${num(gold)})`) : '',
    milestones, readyCount: view.claimable.length, unlocks,
  };
}

const ALLOC_CODE: Readonly<Record<string, [string, Tone]>> = {
  ok: ['', 'ok'], duplicate: ['', 'ok'],
  no_gold: ['골드가 부족해요.', 'err'], denied: ['마을에서만 할 수 있어요.', 'warn'], readonly: ['지금은 바꿀 수 없어요(저장 잠금 또는 다른 창에서 거래 중).', 'err'],
  save_failed: ['저장하지 못해 취소했어요. 다시 시도해 주세요.', 'err'], locked: ['아직 열리지 않았어요.', 'warn'], capped: ['아직 열리지 않은 단계예요.', 'warn'],
  need15: ['+15 장비를 장착한 부위만 초월할 수 있어요.', 'warn'], no_stones: ['최상 강화석이 부족해요.', 'err'], max: ['최대 초월 단계예요.', 'warn'], unknown_item: ['그 부위에 장착한 장비가 없어요.', 'warn'],
};
/** 마지막 숙련·초월 명령 결과 문구(숙련 창에는 배분·이정표만 보여 준다) */
export function masteryMessage(last: MasteryView['last'] | undefined, baseSeq: number): { text: string; tone: Tone } | null {
  if (!last || last.seq <= baseSeq || last.kind === 'transcend') return null;
  if (last.code === 'ok') {
    if (last.kind === 'claim') return { text: '이정표 보상을 받았어요.', tone: 'ok' };
    return { text: last.gold > 0 ? `적용했어요. 재분배 비용 ${num(last.gold)}골드를 냈어요.` : '적용했어요.', tone: 'ok' };
  }
  const m = ALLOC_CODE[last.code];
  return m && m[0] ? { text: m[0], tone: m[1] } : { text: '할 수 없어요.', tone: 'warn' };
}
/** 초월 결과 문구(대장간 초월 구역) */
export function transcendMessage(last: MasteryView['last'] | undefined, baseSeq: number): { text: string; tone: Tone } | null {
  if (!last || last.seq <= baseSeq || last.kind !== 'transcend') return null;
  const slot = last.slot ? SLOT_KO[last.slot] : '';
  if (last.code === 'ok') {
    return last.ok
      ? { text: `${slot} 초월 +${15 + last.to} 성공! (확률 ${pctText(last.chance)})`, tone: 'ok' }
      : { text: `${slot} 초월 +${16 + last.from} 실패 — 단계는 그대로예요 (확률 ${pctText(last.chance)}, 다음엔 천장이 쌓여요)`, tone: 'warn' };
  }
  const m = ALLOC_CODE[last.code];
  return { text: m && m[0] ? m[0] : '할 수 없어요.', tone: m ? m[1] : 'warn' };
}

// ── 대장간 초월 구역 ─────────────────────────────────
export interface TranscendCardModel {
  readonly slot: EquipSlot;
  readonly slotName: string;
  readonly step: number;
  /** 지금 표시 단계: +15(0) · +16 … +20 */
  readonly nowLabel: string;
  readonly maxed: boolean;
  readonly active: boolean;
  /** 다음 단계(16..20, 최대면 0) */
  readonly to: number;
  readonly chanceText: string;
  readonly baseText: string;
  readonly pityText: string;
  readonly failsText: string;
  readonly stonesText: string;
  readonly goldText: string;
  readonly effectText: string;
  readonly canTry: boolean;
  /** 시도할 수 없는 이유(시도할 수 있으면 '') */
  readonly lock: string;
  readonly tryLabel: string;
  /** 정상 상태가 아닌(잠김) 이유 종류 */
  readonly lockKind: '' | 'max' | 'need15' | 'gate' | 'stones' | 'gold' | 'locked';
}
export interface TranscendModel { readonly ready: boolean; readonly cards: readonly TranscendCardModel[]; readonly tryable: number; readonly rule: string }

function gateText(to: number): string {
  return `${gateLabel(to)} 필요`;
}
export const TRANSCEND_RULE = `실패해도 단계가 내려가지 않고 재료만 소모돼요. 실패마다 확률이 +${Math.round(TRANSCEND_PITY.step * 100)}%씩 쌓여(최대 ×${TRANSCEND_PITY.max}) 천장이 있어요.`;

/** 6부위 초월 카드. stones = 강화석 하·중·상·최상 보유, gold = 보유 골드, usable = 대장간 창이 열려 있고 저장이 정상 */
export function transcendModel(view: MasteryView | undefined, stones: readonly number[], gold: number, usable: boolean): TranscendModel {
  if (!view) return { ready: false, cards: [], tryable: 0, rule: TRANSCEND_RULE };
  const cards = view.slots.map((s): TranscendCardModel => {
    const maxed = s.step >= TRANSCEND_MAX;
    const to = maxed ? 0 : 16 + s.step;
    const step = maxed ? TRANSCEND_STEPS[TRANSCEND_MAX - 1] : TRANSCEND_STEPS[s.step];
    const have = stones[TRANSCEND_STONE_TIER] ?? 0;
    const conv = planStoneConversion(stones, TRANSCEND_STONE_TIER, Math.max(0, step.stones - have));
    const need = maxed ? 0 : step.gold + (conv.ok ? conv.fee : 0);
    const mult = maxed ? 1 : Math.min(TRANSCEND_PITY.max, 1 + TRANSCEND_PITY.step * s.fails);
    const kind = TRANSCEND_SLOT_KIND[SLOT_ORDER.indexOf(s.slot)];
    let lockKind: TranscendCardModel['lockKind'] = '', lock = '';
    if (maxed) { lockKind = 'max'; lock = '최대 초월 +20'; }
    else if (!s.active) { lockKind = 'need15'; lock = '+15 장비를 장착해야 해요'; }
    else if (s.step >= view.unlock) { lockKind = 'gate'; lock = gateText(to); }
    else if (!conv.ok) { lockKind = 'stones'; lock = '최상 강화석이 부족해요'; }
    else if (gold < need) { lockKind = 'gold'; lock = '골드가 부족해요'; }
    else if (!usable) { lockKind = 'locked'; lock = '대장간에서만 할 수 있어요'; }
    const effectPer = kind === 'atk' ? `단계당 공격 +${pctText(TRANSCEND_EFFECT.atkPerStep)}` : `단계당 HP +${pctText(TRANSCEND_EFFECT.hpPerStep)}`;
    return {
      slot: s.slot, slotName: SLOT_KO[s.slot], step: s.step, nowLabel: `+${15 + s.step}`, maxed, active: s.active, to,
      chanceText: maxed ? '-' : pctText(s.chance), baseText: maxed ? '-' : pctText(step.chance),
      pityText: maxed ? '-' : `천장 ×${(Math.round(mult * 100) / 100).toFixed(2)}`, failsText: maxed ? '' : `이 단계 실패 ${s.fails}회`,
      stonesText: maxed ? '' : `최상 강화석 ${num(have)}/${num(step.stones)}${have < step.stones && conv.ok ? ' (아래 등급 변환)' : ''}`,
      goldText: maxed ? '' : `골드 ${num(gold)}/${num(need)}`,
      effectText: effectPer,
      canTry: lockKind === '', lock, tryLabel: maxed ? '최대' : `초월 +${to} 시도`, lockKind,
    };
  });
  return { ready: true, cards, tryable: cards.filter((c) => c.canTry).length, rule: TRANSCEND_RULE };
}

// ── 배치(폭 → 칸) ───────────────────────────────────
/** 눌림 칸 최소 한 변(설계 px). rpg.ts가 터치에서 화면 기준 44px이 되게 --dh-btn-min을 올린다 */
export const BTN = 44;
export interface Rect { readonly x: number; readonly y: number; readonly w: number; readonly h: number }
export function rectsOverlap(a: Rect, b: Rect, eps = 0.5): boolean {
  return a.x < b.x + b.w - eps && b.x < a.x + a.w - eps && a.y < b.y + b.h - eps && b.y < a.y + a.h - eps;
}

/** 능력치 한 줄: 글 | − | 값 | + (한 줄 높이 BTN + 위아래 여백) */
export interface StatRowLayout { readonly rowW: number; readonly rowH: number; readonly text: Rect; readonly dec: Rect; readonly val: Rect; readonly inc: Rect }
export function statRowLayout(bodyW: number): StatRowLayout {
  const pad = 10, gap = 8, valW = 40;
  const rowW = Math.max(240, Math.floor(bodyW));
  const rowH = BTN + pad;
  const textW = rowW - pad * 2 - gap * 3 - BTN * 2 - valW;
  const y = (rowH - BTN) / 2;
  return {
    rowW, rowH,
    text: { x: pad, y, w: textW, h: BTN },
    dec: { x: pad + textW + gap, y, w: BTN, h: BTN },
    val: { x: pad + textW + gap * 2 + BTN, y, w: valW, h: BTN },
    inc: { x: pad + textW + gap * 3 + BTN + valW, y, w: BTN, h: BTN },
  };
}

/** 초월 카드 격자: 폭에 따라 1~2열. 카드 안 단추는 카드 폭 전체 */
export interface TranscendLayout { readonly cols: number; readonly cardW: number; readonly gap: number; readonly btnW: number; readonly btnH: number; readonly rows: number; readonly totalW: number }
export function transcendLayout(bodyW: number, cards = 6): TranscendLayout {
  const w = Math.max(240, Math.floor(bodyW)), gap = 8;
  const cols = w >= 460 ? 2 : 1;
  const cardW = Math.floor((w - gap * (cols - 1)) / cols);
  return { cols, cardW, gap, btnW: cardW - 20, btnH: 48, rows: Math.ceil(cards / cols), totalW: cols * cardW + gap * (cols - 1) };
}
/** 카드 i의 격자 위치(카드 높이 cardH 가정) — 겹침 시험용 */
export function transcendCardRect(l: TranscendLayout, i: number, cardH: number): Rect {
  const c = i % l.cols, r = Math.floor(i / l.cols);
  return { x: c * (l.cardW + l.gap), y: r * (cardH + l.gap), w: l.cardW, h: cardH };
}
export { TRANSCEND_MAX };
